import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";

import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import EditIcon from "@MasterBluePrint/Icons/EditIcon";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import ShipToSelector, { ShipToSelectorStyles } from "@MasterBluePrint/Overrides/Components/ShipToSelector";
import ShippingAddressForm from "@MasterBluePrint/Overrides/Widgets/CheckoutShipping/ShippingAddressForm";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    address: ShipToModel;
    currentBillToId: string | undefined;
    newAddress: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries?: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
    onFormSubmit: (event: React.FormEvent<HTMLFormElement>, newAddress: ShipToModel) => void;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const isCheckoutShippingPage = location.pathname.toLowerCase().startsWith("/checkoutshipping");
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        countries: getCurrentCountries(state),
        currentShipTo: getCurrentShipToState(state).value,
        isCheckoutShippingPage,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    setCurrentShipTo,
    loadCurrentShipTos
};
type ContextType = {
    isSettingShipTo: boolean;
    setIsSettingShipTo: (value: boolean) => void;
};
export const AddressBookContext = createContext<ContextType>({
    isSettingShipTo: false,
    setIsSettingShipTo: value => { },
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    customAddressBookModal?: InjectableCss;
    closeContainer?: InjectableCss;
    addressBookModalContinueButton?: ButtonPresentationProps;
    wideCreateNewAddressButton?: InjectableCss;
    buttonWrapper?: InjectableCss;
    selectAddressButton?: InjectableCss;
    addressForm?: CustomerAddressFormStyles;
    useShippingAddressGridItem?: GridItemProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    container: {
        gap: 20,
        css: css`
            max-width: 50%;
            flex-basis: 50%;
            
            address {
                color: ${themeColors.colorAddress};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0.0313rem;
                line-height: 1.5rem;

                p, span {
                    color: ${themeColors.colorAddress};
                }
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
            color: #002554;
        `,
    },
    centeringWrapper: {
        css: css`
            background: rgba(0, 37, 84, 0.2);
            height: 100%;
            width: 100%;
            position: fixed;
            z-index: 99;
            left: 0;
            top: 0;
            display: flex;
            align-items: center;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            justify-content: space-between;

            h2.section-heading,
            h3.section-heading {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size20};
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0;
                line-height: 1.375rem;
                margin-bottom: 1.25rem;
            }
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size18};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 1.25rem;
            padding-left: 0.9375rem;
            margin-right: 1.875rem;
            border-left: 0.0625rem solid ${themeColors.colorBorderNew};
             svg {
                width: 1.25rem;
                height: 1.25rem;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        margin-right: 0;
                    `,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    addressBookModal: {
        sizeVariant: "medium"
    },
    customAddressBookModal: {
        css: css`
            div[class^='ModalContent'], div[class*=' ModalContent']{
                position: relative;
                padding-bottom: 6.9375rem;
                height: calc(100vh - 6.3125rem);

                @media screen and (max-width: 767px) {
                    padding-bottom: 6.9375rem;
                    height: calc(100vh - 5.125rem);
                }
                @media screen and (min-width: 370px) and (max-width: 480px) {
                    padding-bottom: 6.9375rem;
                    height: calc(100vh - 4.125rem);
                }
                @media screen and (max-width: 360px) {
                    padding-bottom: 6.9375rem;
                    height: calc(100vh - 3.3125rem);
                }
            }

            iframe {
                width: 99.4%;
            }

            &.select-address {
                div[class^='ModalContent'], div[class*=' ModalContent']{
                    position: relative;
                    padding-bottom: 9.5rem;
                    height: calc(100vh - 9.5rem);
    
                    @media screen and (max-width: 47.9375rem) {
                        padding-bottom: 7.375rem;
                        height: calc(100vh - 7.375rem);

                        .btn-wrapper {
                            > div {
                                margin-top: 0;
                                margin-bottom: 0;
                            }
                        }
                    }
                    @media screen and (max-width: 360px) {
                        height: calc(100vh - 6.5rem);                           
                    }                    
                    @media screen and (min-width: 371px) and (max-width: 480px) {
                        height: calc(100vh - 7.375rem);
                    }
                    @media screen and (max-width: 340px) {
                        height: calc(100vh - 2.5rem);
                    }
                }

                &.no-address {
                    div[class^='ModalContent'], div[class*=' ModalContent']{
                        position: relative;
                        padding-bottom: 9.5rem;
                        height: 98vh;
                        .cancel-wrapper {
                            position: fixed;
                            width: 35.625rem;
                            right: 0;
                            background: ${themeColors.colorWhite};
                            height: 2.5rem;
                            bottom: 0;
                            box-shadow: 0 -0.125rem 0.375rem 0 rgba(0,0,0,0.31);
                            padding-bottom: 2.875rem;
                            padding-top: 2.875rem;

                        }
                        .cancel-btn {
                            position: absolute;
                            bottom: 1.5625rem;
                            left: auto;
                            right: 17.75rem;
                        }
        
                        @media screen and (max-width: 767px) {
                            height: 98vh;

                            .cancel-wrapper {
                                width: 100%;
                            }
                            .cancel-btn {
                                position: absolute;
                                bottom: 1.375rem;
                                left: 45%;
                                right: auto;
                            }
                        }
                    }
                }
            }

            .new-address-form,
            .edit-address-form {
                padding: 0 1.5625rem 1.5625rem 1.5625rem;
            }
                
            .border-seperator {
                border-bottom: 0.0625rem solid color: ${themeColors.colorBorderSeperator}
                padding-top: 0;
                margin-top: 1.25rem;
                margin-bottom: 1.25rem;
                width: 100%;
                display: block;
            }
            > div {
                border: 0.0625rem solid rgba(7,15,44,0.08);
                border-radius: 0.0625rem;
                background-color: ${themeColors.colorWhite};
                max-width: 35.625rem;
                margin-right: 0;
                margin-left: auto;
                margin-top: 0;
                max-height: 100vh;

                @media (max-width: 767px) {
                    max-width: 100%;
                    margin-right: 0;
                    margin-left: 0;
                }

                #modalTitle {
                    border-bottom: none;
                    padding-bottom: 0;
                    display: none;

                    h4 {
                        color: ${themeColors.colorPrimary};
                        font-family: 'Roboto', sans-serif;
                        font-size: ${themeFontSizes.size18};
                        font-weight: ${themeFontWeights.fontWeightMedium};
                        letter-spacing: 0;
                        line-height: 1.9375rem;
                        padding-top: 2.8125rem;
                        position: relative;

                        &:after {
                            height: 0.125rem;
                            width: 2.75rem;
                            background-color:  ${themeColors.colorRed};
                            content: '';
                            display: inline-block;
                            position: absolute;
                            bottom: -0.1875rem;
                            left: 0.0625rem;
                        }
                    }
                }

                .modal-title {
                    border-bottom: none;
                    padding-bottom: 0;

                    h2 {
                        display: none;

                        &.current {
                            display: block;
                            color: ${themeColors.colorPrimary};
                            font-family: 'Roboto', sans-serif;
                            font-size: ${themeFontSizes.size18};
                            font-weight: ${themeFontWeights.fontWeightMedium};
                            letter-spacing: 0;
                            line-height: 1.9375rem;
                            padding-top: 2.8125rem;
                            position: relative;
                            margin-bottom: 2.1875rem;
    
                            &:after {
                                height: 0.125rem;
                                width: 2.75rem;
                                background-color:  ${themeColors.colorRed};
                                content: "";
                                display: inline-block;
                                position: absolute;
                                bottom: -0.1875rem;
                                left: 0.0625rem;
                            }
                        }
                        
                    }

                    button[aria-labelledby="close-modal"] {
                        display: none;
                    }
                }
            }
            .toggle-content {
                display: none;

                &.active {
                    display: block;
                }
            }
        `,
    },
    addressBookModalButtonsWrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            margin-top: 2rem;
            margin-bottom: 0;
            justify-content: center;
            align-items: start;
        `,
    },
    addressBookModalCancelButton: {
        css: css`
            color: color: ${themeColors.colorGreyCancel};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size15};
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            padding: 0;
            background: none;
            border: none;
            span {
                font-weight: normal !important;
            }

            &:hover,
            &:active, 
            &:focus {
                background: none !important;
                border: none !important;
                color: ${themeColors.colorPrimary};
            }

            &.cancel-btn {
                position: fixed;
                bottom: 1.8rem;
                left: auto;
                right: 25.625rem;

                @media screen and (max-width: 767px) {
                    left: 15%;
                    right: auto;
                }
            }
        `,
    },
    addressBookModalContinueButton: {
        variant: "primary",
        css: css`
            background-color: ${themeColors.colorPrimary};
            border-color: ${themeColors.colorPrimary};
            color: ${themeColors.colorWhite};
            text-transform: uppercase;
            margin-bottom: 0;
            padding: 0 3.75rem;
            width: auto;
            border-radius: 0.125rem;

            &:hover,
            &:active, 
            &:focus {
                background-color: ${themeColors.colorPrimary} !important;
                border-color: ${themeColors.colorPrimary} !important;
                color: ${themeColors.colorWhite};
            }
        `,
    },
    oneTimeAddressGridItem: { width: 12 },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 1.25rem;
        `,
    },
    closeContainer: {
        css: css`
           position: absolute;
           left: 1.8rem;
           top: 1.5rem;
           cursor: pointer;
           z-index: 9;

           .close-label {
                color: ${themeColors.colorMidGrey}
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: .525rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1rem;
                height: 1rem;
                svg {
                    height: 1rem;
                    width: 1rem;
                    color: ${themeColors.colorMidGrey}
                }
            }
            @media screen and (max-width: 990px) {
                right: 1.25rem;
            }
        `,
    },
    wideCreateNewAddressButton: {
        css: css`
            margin-left: 0;
            flex-basis: 50%;
            max-width: 50%;
            margin-bottom: 0;
            width: 50%;
            color: ${themeColors.colorPrimary} !important;
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.1875rem;
            text-align: center;
            padding: 0.625rem 0;
            display: inline-block;
            background: none !important;
            border: none !important;
            cursor: pointer;
            vertical-align: middle;
            span {
                font-size: ${themeFontSizes.size14};
                font-weight: ${themeFontWeights.fontWeightMedium};
            }
            &.current {
                color: ${themeColors.colorWhite} !important;
                background-color: ${themeColors.colorButtonHover} !important;

                svg {
                    color: ${themeColors.colorWhite} !important; 
                }
            }

            @media (max-width: 767px) {
                height: auto;
                text-align: left;
                padding-left: 0.9375rem;
                position: relative;

                .text {
                    max-width: 6.875rem;
                    display: inline-block;
                    vertical-align: bottom;
                    text-align: left;
                }

                .arrows {
                    display: inline-block;
                    vertical-align: bottom;
                    line-height: 1rem;
                    height: 1.125rem;
                    position: absolute;
                    right: 0.9375rem;
                    bottom: 0.625rem;
                }

                .up-arrow {
                    display: none;
                }


                &.current {
                    .down-arrow {
                        display: none;
                    }

                    .up-arrow {
                        display: inline-block;
                    }
                }
            }
            
        `,
    },
    selectAddressButton: {
        css: css`
            width: 50%;
            flex-basis: 50%;
            max-width: 50%;
            margin-bottom: 0;
            width: 50%;
            color: ${themeColors.colorPrimary} !important;
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.1875rem;
            text-align: center;
            padding: 0.625rem 0;
            display: inline-block;
            background: none !important;
            border: none !important;
            cursor: pointer;
            vertical-align: middle;
            span {
                font-size: ${themeFontSizes.size14};
                font-weight: ${themeFontWeights.fontWeightMedium};
            }
            &.current {
                color: ${themeColors.colorWhite} !important;
                background-color: ${themeColors.colorButtonHover} !important;

                svg {
                    color: ${themeColors.colorWhite} !important; 
                }
            }

            @media (max-width: 767px) {
                height: auto;
                text-align: left;
                padding-left: 0.9375rem;
                position: relative;

                .text {
                    max-width: 6.875rem;
                    display: inline-block;
                    vertical-align: bottom;
                    text-align: left;
                }

                .arrows {
                    display: inline-block;
                    vertical-align: bottom;
                    line-height: 1rem;
                    height: 1.125rem;
                    position: absolute;
                    right: 0.9375rem;
                    bottom: 0.625rem;
                }

                .up-arrow {
                    display: none;
                }

                &.current {
                    .down-arrow {
                        display: none;
                    }

                    .up-arrow {
                        display: inline-block;
                    }
                }
            }
        `,
    },
    buttonWrapper: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            border: 0.0625rem solid ${themeColors.colorBorderCyanBlue};
            border-radius: 0.125rem;
            background-color: ${themeColors.colorBorderCyanBlueNew};
            margin-bottom: 1.25rem;
            padding: 0.25rem 0.4375rem;
        `,
    },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;

            label {
                color: ${themeColors.colorAddress} !important;
                font-family: 'Lato',sans-serif !important;
                font-size: ${themeFontSizes.size15} !important;
                letter-spacing: 0.17px !important;
                line-height: 1.125rem;
            }
        `,
    },
};

const ShippingAddress: FC<Props> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    isUseBillingAddressDisabled,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    onFormSubmit,
    shipToAddressFields,
    setCurrentShipTo,
    currentShipTo,
    loadCurrentShipTos,
    isCheckoutShippingPage,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));
    const [selectShippingAddress, setSelectShippingAddress] = useState(true);
    const [addNewAddress, setAddNewAddress] = useState(false);
    const [editAddress, seteditAddress] = useState(false);
    const [defaultShipToAddress, setDefaultShipToAddress] = useState(false);
    const [selectedShipaddress, setSelectedShipaddress] = useState<ShipToModel | undefined>(undefined);
    const [showShippingAddressForm, setShowShippingAddressForm] = useState(true);
    let featureFlags: string[] = ["TEUS-7942"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enableVerticalAddressLayoutCheckout = flagValues["TEUS-7942"];

    const shippingCountries = currentUserIsGuest
        ? countries?.filter(country => country.abbreviation === "US")
        : countries;

    useEffect(() => {
        setShowShippingAddressForm(!enableVerticalAddressLayoutCheckout);
    }, [enableVerticalAddressLayoutCheckout]);
 
    useEffect(() => {
        if (addressBeingEdited) {
            // setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    useEffect(() => {
        if (currentShipTo) {
            setDefaultShipToAddress(address.id === currentShipTo.id);
        }
    }, []);

    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
        setUseOneTimeAddress({
            useOneTimeAddress: value,
        });

    const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setUseBillingAddress({
            useBillingAddress: value,
        });
    };

    const useBillingAddressChangeHandlerForCheckBox: CheckboxProps["onChange"] = (_, value) => {
        if (value === true) {
            setUseBillingAddress({
                useBillingAddress: true,
            });
            setShowShippingAddressForm(false);
        } else {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
            setShowShippingAddressForm(true);
        }
    };

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        if (event.target.value === "onetime") {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
            setShowShippingAddressForm(true);
        } else if (event.target.value === "billing") {
            setUseBillingAddress({
                useBillingAddress: true,
            });
            setShowShippingAddressForm(false);
        }
    };

    const selectAddressClickHandler = () => {
        setIsCustomerSelectorModalOpen(true);
        checkIfAddressExist();
    }

    const customerSelectorModalCloseHandler = () => {
        setIsCustomerSelectorModalOpen(false);

        setAddNewAddress(false);
        setSelectShippingAddress(true);
        seteditAddress(false);
        checkIfAddressExist();
    }

    const [isClickHappened, setIsClickHappened] = useState(false)

    useEffect(() => {

        // Replace 'targetElement' with the specific DOM element you want to track
        const someIdValue = 'checkoutShipping_shippingAddress'
        const elements = document.querySelectorAll(`[data-test-selector="${someIdValue}"]`);

        // Attach an event listener to track updates


        const observer = new MutationObserver(() => {
            setIsClickHappened(false)
        })

        if (elements?.[0]) {
            observer.observe(elements?.[0], { attributes: true, childList: true, subtree: true })
        }

        // Clean up the event listener on component unmount
        return () => {
            if (elements?.[0]) {
                observer.disconnect();
            }
        };
    }, []); // Ensure this effect runs only once

    const customerNewSelectorModalCloseHandler = () => {
        // setIsCustomerSelectorModalOpen();
        if (selectedShipaddress) {
            onChange(selectedShipaddress);
            if (currentShipTo) {
                setDefaultShipToAddress(selectedShipaddress.id === currentShipTo.id);
            }
            customerSelectorModalCloseHandler();
            setIsClickHappened(true)
        }
        setAddNewAddress(false);
        setSelectShippingAddress(true);
        seteditAddress(false);

    }

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        // customerNewSelectorModalCloseHandler();
        // onChange(shipTo);
        // if(currentShipTo)
        // {
        //     setDefaultShipToAddress(shipTo.id===currentShipTo.id);
        // }
        setSelectedShipaddress(shipTo);
    };



    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const finalizeNewAddress = (address: ShipToModel) => {
        onChange(address);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: "Address has been created.",
        });
    };

    // Address validation (Piyovi AVS) now runs inside CustomerAddressForm on submit;
    // this handler only persists the chosen address.
    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) =>
        finalizeNewAddress(address);

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
        seteditAddress(true);
        setAddNewAddress(true);
        setSelectShippingAddress(false);
        setAddNewAddress(false);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const finalizeEditAddress = (address: ShipToModel) => {
        onChange(address);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: "Address updated.",
        });
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) =>
        finalizeEditAddress(address);

    const checkIfAddressExist = () => {
        setTimeout(() => {
            if (document.getElementsByClassName('no-address').length) {
                const isClassPresent = document.getElementsByClassName('no-address');
                if (isClassPresent.length > 0) {
                    document.querySelectorAll('[data-test-selector="checkoutShipping_addressModal"]')[0].classList.add('no-address');
                } else {
                    document.querySelectorAll('[data-test-selector="checkoutShipping_addressModal"]')[0].classList.remove('no-address');
                }
            }
        }, 1000);
    };

    const showShippingAddress = () => {
        checkIfAddressExist();
        setAddNewAddress(false);
        setSelectShippingAddress(true);
        seteditAddress(false);
        checkIfAddressExist();
    };

    const showNewAddress = () => {
        setAddNewAddress(true);
        setSelectShippingAddress(false);
        checkIfAddressExist();
    };

    return (
        <>
            {isClickHappened && <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>}
            <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
                <GridItem {...styles.headingGridItem} style={enableVerticalAddressLayoutCheckout ? { justifyContent: 'left', gap: '15px' } : {}}>
                    <Typography {...styles.headingText} as="h3" >
                        {translate("Shipping Information")}
                    </Typography>

                    {enableVerticalAddressLayoutCheckout && (
                        <Checkbox
                            {...styles.oneTimeAddressCheckbox}
                            onChange={useBillingAddressChangeHandlerForCheckBox}
                            checked={shippingAddressFormState ? shippingAddressFormState.address.id === currentBillToId : false}
                        >
                            {translate("Use Billing Address")}
                        </Checkbox>
                    )}

                    {(allowCreateNewShipToAddress || !!newAddress || !!address) && (
                        <Link
                            {...styles.selectSavedAddressLink}
                            type="button"
                            onClick={selectAddressClickHandler}
                            data-test-selector="checkoutShipping_selectSavedAddress"
                        >
                            <Icon src={EditIcon} />
                            <VisuallyHidden>{translate("Select Saved Address")}</VisuallyHidden>
                        </Link>
                    )}
                    <Modal
                        {...styles.addressBookModal}
                        {...styles.customAddressBookModal}
                        headline={translate("Select a shipping address or  add a new one")}
                        isOpen={isCustomerSelectorModalOpen}
                        handleClose={customerSelectorModalCloseHandler}
                        data-test-selector="checkoutShipping_addressModal"
                        className={`ship-modal ${selectShippingAddress ? "select-address" : ""}`}
                    >
                        <Typography as="div" onClick={() => customerSelectorModalCloseHandler()} {...styles.closeContainer}>
                            <span className="close-label">Close</span>
                            <Icon className="icon-close" src="XCircle" />
                        </Typography>


                        <Typography as="div" className="modal-title">
                            <Typography as="h2" className={`${editAddress ? "current" : ""}`}>
                                {translate("Edit shipping address")}
                            </Typography>

                            <Typography as="h2" className={`${addNewAddress ? "current" : ""}`}>
                                {translate("Add New Address")}
                            </Typography>

                            <Typography as="h2" className={`${selectShippingAddress ? "current" : ""}`}>
                                {translate("Select a shipping address")}
                            </Typography>
                        </Typography>

                        {!editAddress &&
                            <StyledWrapper {...styles.buttonWrapper}>
                                <Button
                                    {...styles.selectAddressButton}
                                    data-test-selector="customerSelectorToolbar_selectNewAddress"
                                    className={`${selectShippingAddress ? 'current' : ''}`}
                                    onClick={showShippingAddress}
                                >
                                    <StyledWrapper as="span" className="text">{translate("Select a shipping address")}</StyledWrapper>
                                    <Hidden above="sm">
                                        <StyledWrapper as="span" className="arrows">
                                            <Icon className="up-arrow" src="ChevronUp" size={16} />
                                            <Icon className="down-arrow" src="ChevronDown" size={16} />
                                        </StyledWrapper>
                                    </Hidden>
                                </Button>
                                <>
                                    <Button
                                        {...styles.wideCreateNewAddressButton}
                                        data-test-selector="customerSelectorToolbar_createNewAddress"
                                        className={`${addNewAddress || editAddress ? 'current' : ''}`}
                                        onClick={showNewAddress}
                                    >
                                        <StyledWrapper as="span" className="text">{translate("Add New Address")}</StyledWrapper>
                                        <Hidden above="sm">
                                            <StyledWrapper as="span" className="arrows">
                                                <Icon className="up-arrow" src="ChevronUp" size={16} />
                                                <Icon className="down-arrow" src="ChevronDown" size={16} />
                                            </StyledWrapper>
                                        </Hidden>
                                    </Button>
                                </>
                            </StyledWrapper>
                        }

                        {!editAddress &&
                            <StyledWrapper as="span" className="border-seperator"></StyledWrapper>
                        }
                        <StyledWrapper className={`toggle-content ${selectShippingAddress ? 'active' : ''}`}>
                            <ShipToSelector
                                currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                                currentBillToId={currentBillToId}
                                newAddress={newAddress}
                                onSelect={selectCustomerHandler}
                                onEdit={editCustomerHandler}
                                onCreateNewAddressClick={newAddress && createNewAddressHandler}
                                extendedStyles={styles.shipToSelector}
                                customerSelectorModalCloseHandler={customerNewSelectorModalCloseHandler}
                            />
                            <StyledWrapper {...styles.addressBookModalButtonsWrapper} className="cancel-wrapper">
                                <Button
                                    {...styles.addressBookModalCancelButton}
                                    onClick={customerSelectorModalCloseHandler}
                                    data-test-selector="addressModal_cancel"
                                    className="cancel-btn"
                                >
                                    {translate("Cancel")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>

                        <StyledWrapper className={`toggle-content ${addNewAddress || editAddress ? 'active' : ''}`}>
                            {addNewAddress && shipToAddressFields && countries && (
                                <StyledWrapper className="new-address-form">
                                    <CustomerAddressForm
                                        address={newAddress}
                                        countries={countries}
                                        addressFieldDisplayCollection={shipToAddressFields}
                                        onSubmit={newShippingAddressFormSubmitHandler}
                                        onCancel={customerSelectorModalCloseHandler}
                                        extendedStyles={styles.addressForm}
                                    />
                                </StyledWrapper>
                            )}
                            {editAddress && addressBeingEdited && shipToAddressFields && countries && (
                                <StyledWrapper className="edit-address-form">
                                    <CustomerAddressForm
                                        address={addressBeingEdited}
                                        countries={countries}
                                        addressFieldDisplayCollection={shipToAddressFields}
                                        onSubmit={editAddressFormSubmitHandler}
                                        onCancel={customerSelectorModalCloseHandler}
                                        extendedStyles={styles.addressForm}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>


                    </Modal>
                    {newAddress && (
                        <CreateNewAddressModal
                            newAddress={newAddress}
                            isModalOpen={isCreateNewAddressModalOpen}
                            onModalClose={createNewAddressModalCloseHandler}
                            onFormSubmit={newShippingAddressFormSubmitHandler}
                        />
                    )}
                    {addressBeingEdited && (
                        <EditExistingAddressModal
                            newAddress={addressBeingEdited}
                            isModalOpen={isEditAddressModalOpen}
                            onModalClose={editAddressModalCloseHandler}
                            onFormSubmit={editAddressFormSubmitHandler}
                        />
                    )}
                </GridItem>
                {/* <GridItem {...styles.useShippingAddressGridItem} className="use-bill-address">
               <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                    <Checkbox
                        {...styles.oneTimeAddressCheckbox}
                        onChange={selectChangeHandler}
                        checked={defaultShipToAddress}
                        disabled={currentUserIsGuest}
                    >
                        {translate("Make this my default address")}
                    </Checkbox>
                </CheckboxGroup>
            </GridItem> */}
                {isCheckoutShippingPage && !enableVerticalAddressLayoutCheckout && (
                    <>
                        {oneTimeAddress && showUseBillingAddress ? (
                            <GridItem {...styles.oneTimeAddressGridItem}>
                                <RadioGroup
                                    value={
                                        shippingAddressFormState
                                            ? shippingAddressFormState.address.oneTimeAddress
                                                ? "onetime"
                                                : shippingAddressFormState.address.id === currentBillToId
                                                    ? "billing"
                                                    : ""
                                            : ""
                                    }
                                    onChangeHandler={addressTypeChangeHandler}
                                >
                                    <Radio value="onetime" data-test-selector="checkoutShipping_useOneTimeAddress">
                                        {translate("Ship to One-Time Address")}
                                    </Radio>
                                    <Radio value="billing">{translate("Use Billing Address")}</Radio>
                                </RadioGroup>
                            </GridItem>
                        ) : (
                            <>
                                {oneTimeAddress && (
                                    <GridItem {...styles.oneTimeAddressGridItem}>
                                        <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                            <Checkbox
                                                {...styles.oneTimeAddressCheckbox}
                                                onChange={oneTimeAddressChangeHandler}
                                                checked={useOneTimeAddress}
                                                disabled={currentUserIsGuest}
                                            >
                                                {translate("Ship to One-Time Address")}
                                            </Checkbox>
                                        </CheckboxGroup>
                                    </GridItem>
                                )}
                                {showUseBillingAddress && (
                                    <GridItem {...styles.oneTimeAddressGridItem}>
                                        <CheckboxGroup {...styles.oneTimeAddressCheckboxGroup}>
                                            <Checkbox
                                                {...styles.oneTimeAddressCheckbox}
                                                onChange={useBillingAddressChangeHandler}
                                                checked={useBillingAddress || isUseBillingAddressDisabled}
                                                disabled={isUseBillingAddressDisabled}
                                            >
                                                {translate("Use Billing Address")}
                                            </Checkbox>
                                        </CheckboxGroup>
                                    </GridItem>
                                )}
                            </>
                        )}
                    </>
                )}

                <GridItem {...styles.addressDisplayAndFormGridItem}>
                    {isCheckoutShippingPage ? (
                        <>
                            {(useOneTimeAddress || isShippingAddressUpdateRequired) && countries && showShippingAddressForm && (
                                <ShippingAddressForm countries={shippingCountries} fieldDisplay={addressFieldDisplayCollection} />
                            )}
                            {!useOneTimeAddress && !currentUserIsGuest && !isShippingAddressUpdateRequired && (
                                <AddressInfoDisplay
                                    {...address}
                                    state={address.state ? address.state.abbreviation : undefined}
                                    country={address.country ? address.country.abbreviation : undefined}
                                    extendedStyles={styles.addressDisplay}
                                />
                            )}
                        </>
                    ) : address && (
                        <AddressInfoDisplay
                            {...address}
                            state={address.state ? address.state.abbreviation : undefined}
                            country={address.country ? address.country.abbreviation : undefined}
                            extendedStyles={styles.addressDisplay}
                        />
                    )}




                </GridItem>
            </GridContainer>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddress);